import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { jwtDecode } from 'jwt-decode'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isGuest: false, // Track if current session is a guest

      // Actions
      login: async (tokenData) => {
        try {
          set({ isLoading: true })
          
          // Decode JWT token using proper library
          const decodedToken = jwtDecode(tokenData.access_token);
          
          // Skip token expiry check for persistent login
          // Token will only be invalidated on manual logout
          
          // Map backend roles to frontend roles for consistency
          const roleMapping = {
            'manager': 'GESTOR',
            'admin': 'GESTOR', 
            'receptionist': 'ATENDENTE',
            'professional': 'PROFISSIONAL',
            'client': 'CLIENTE'
          };
          
          const backendRole = decodedToken.role || 'client';
          const mappedRole = roleMapping[backendRole] || backendRole;
          
          // Standardize user data structure to match mobile app
          const user = {
            id: decodedToken.user_id || decodedToken.sub,
            email: decodedToken.sub || decodedToken.email, // 'sub' is typically the email
            fullName: decodedToken.full_name || decodedToken.name || 'User',
            role: mappedRole,
            originalRole: backendRole, // Keep original for debugging
            isActive: decodedToken.is_active !== false // Default to true if not specified
          }

          // Store token in the standardized location for API client
          localStorage.setItem('authToken', tokenData.access_token);

          set({
            user,
            token: tokenData.access_token,
            isAuthenticated: true,
            isLoading: false
          })

          return user
        } catch (error) {
          set({ isLoading: false })
          throw new Error('Failed to process login token')
        }
      },

      logout: () => {
        // Clear both storage locations
        localStorage.removeItem('authToken');
        localStorage.removeItem('torri-auth-storage');

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isGuest: false
        })
      },

      // Login as guest for public booking flow
      loginAsGuest: async () => {
        try {
          set({ isLoading: true })

          // Use the same API base URL as the rest of the app
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/guest-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          })

          if (!response.ok) {
            const errorText = await response.text()
            console.error('Guest token response error:', response.status, errorText)
            throw new Error(`Failed to get guest token: ${response.status}`)
          }

          const tokenData = await response.json()
          console.log('Guest token received successfully')
          const decodedToken = jwtDecode(tokenData.access_token)

          const user = {
            id: decodedToken.user_id,
            email: null, // Guests don't have email
            fullName: decodedToken.full_name || 'Guest',
            role: 'GUEST',
            isActive: true,
            isGuest: true
          }

          // Store token for API calls
          localStorage.setItem('authToken', tokenData.access_token)

          set({
            user,
            token: tokenData.access_token,
            isAuthenticated: true,
            isGuest: true,
            isLoading: false
          })

          return user
        } catch (error) {
          console.error('Failed to login as guest:', error)
          set({ isLoading: false })
          throw error
        }
      },

      // Check if current user is a guest
      isGuestUser: () => {
        const state = get()
        return state.isGuest || state.user?.isGuest || state.user?.role === 'GUEST'
      },

      setLoading: (loading) => {
        set({ isLoading: loading })
      },

      // Helper to get current user
      getCurrentUser: () => get().user,
      
      // Helper to get auth token
      getToken: () => get().token,

      // Validate stored token on app startup
      validateStoredToken: () => {
        const state = get();
        let token = state.token;
        
        // If no token in state, try to get from localStorage
        if (!token) {
          token = localStorage.getItem('authToken');
          if (!token) {
            // Try fallback location
            const authStorage = localStorage.getItem('torri-auth-storage');
            if (authStorage) {
              try {
                const parsedStorage = JSON.parse(authStorage);
                token = parsedStorage.state?.token;
              } catch (error) {
                // Ignore JSON parsing errors
              }
            }
          }
        }
        
        if (!token) {
          return false;
        }

        try {
          const decodedToken = jwtDecode(token);
          
          // Skip token expiry check for persistent login
          // Users will only be logged out manually
          
          // If token is valid but not in state, restore it
          if (!state.token) {
            
            // Apply the same role mapping as in login
            const roleMapping = {
              'manager': 'GESTOR',
              'admin': 'GESTOR', 
              'receptionist': 'ATENDENTE',
              'professional': 'PROFISSIONAL',
              'client': 'CLIENTE'
            };
            
            const backendRole = decodedToken.role || 'client';
            const mappedRole = roleMapping[backendRole] || backendRole;
            
            const user = {
              id: decodedToken.user_id || decodedToken.sub,
              email: decodedToken.sub || decodedToken.email,
              fullName: decodedToken.full_name || decodedToken.name || 'User',
              role: mappedRole,
              originalRole: backendRole,
              isActive: decodedToken.is_active !== false
            };
            
            // Ensure token is in standardized location
            localStorage.setItem('authToken', token);
            
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false
            });
          }
          
          return true;
        } catch (error) {
          state.logout();
          return false;
        }
      },

      // Update user profile data (similar to mobile app)
      setProfile: (profileData) => {
        const currentUser = get().user;
        
        const updatedUser = {
          ...currentUser,
          ...profileData,
          // Ensure essential fields from JWT are not overwritten
          id: profileData.id || currentUser?.id,
          email: profileData.email || currentUser?.email,
        };

        set({ user: updatedUser });
      },

      // Role-based helper functions
      isClient: () => {
        const user = get().user;
        return user?.role === 'CLIENTE';
      },

      isProfessional: () => {
        const user = get().user;
        return ['PROFISSIONAL', 'ATENDENTE', 'GESTOR'].includes(user?.role);
      },

      hasRole: (roles) => {
        const user = get().user;
        if (!user) return false;
        return Array.isArray(roles) ? roles.includes(user.role) : user.role === roles;
      },

      getUserRole: () => {
        const user = get().user;
        return user?.role || 'CLIENTE';
      },
    }),
    {
      name: 'torri-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest,
      }),
    }
  )
)